import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Add, Remove, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import type { Product } from '../../types';
import CustomerPicker, { PickedCustomer } from '../../components/commercial/CustomerPicker';
import { formatDay, formatMoney } from '../../components/commercial/contact';

type CartLine = { product: Product; qty: number; price: number };

const METHODS = ['Efectivo', 'Transferencia', 'Mercado Pago', 'Tarjeta'];
const DUE_OPTIONS = [7, 15, 30, 60];

const MobileSellPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [cart, setCart] = useState<Record<number, CartLine>>({});
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [paidNow, setPaidNow] = useState(true);
  const [method, setMethod] = useState('Efectivo');
  const [dueInDays, setDueInDays] = useState(30);
  const [displays, setDisplays] = useState(0);
  const [delivered, setDelivered] = useState(true);
  const [prices, setPrices] = useState<Record<number, { price: number; source: string }>>({});

  // Precio del cliente (pactado → su lista → lista por defecto). Al cambiar de cliente se re-precia el carrito.
  useEffect(() => {
    api.resolvePrices(customer?.id)
      .then(rows => {
        const map = Object.fromEntries(rows.map(r => [r.productId, { price: r.price, source: r.source }]));
        setPrices(map);
        setCart(prev => Object.fromEntries(Object.entries(prev).map(([id, l]) => [id, { ...l, price: map[Number(id)]?.price ?? l.price }])));
      })
      .catch(() => setPrices({}));
  }, [customer]);
  const priceOf = (p: Product) => prices[p.id]?.price ?? p.price;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ reference: string; total: number; paidNow: boolean; dueDate?: string | null; warning?: string | null; delivered: boolean } | null>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoadingProducts(true);
      try {
        const res = await api.getProducts({ page: 1, pageSize: 40, search: search || undefined });
        setProducts((res.products || []).filter(p => p.isActive !== false));
      } catch {
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const lines = Object.values(cart);
  const total = useMemo(() => lines.reduce((s, l) => s + l.qty * l.price, 0), [lines]);

  const changeQty = (p: Product, delta: number) => {
    setCart(prev => {
      const current = prev[p.id];
      const qty = (current?.qty || 0) + delta;
      const next = { ...prev };
      if (qty <= 0) delete next[p.id];
      else next[p.id] = { product: p, qty, price: current?.price ?? priceOf(p) };
      return next;
    });
  };

  const setPrice = (id: number, price: number) => {
    setCart(prev => (prev[id] ? { ...prev, [id]: { ...prev[id], price } } : prev));
  };

  const submit = async () => {
    if (!lines.length) { setError('Agregá al menos un producto'); return; }
    if (!paidNow && !customer) { setError('Para vender a cuenta elegí el cliente'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await api.quickSale({
        customerId: customer?.id,
        items: lines.map(l => ({ productId: l.product.id, quantity: l.qty, unitPrice: l.price })),
        paidNow,
        paymentMethod: method,
        dueInDays: paidNow ? undefined : dueInDays,
        displaysDelivered: displays,
        delivered,
      });
      setDone({ reference: res.reference, total: res.total, paidNow: res.paidNow, dueDate: res.paymentDueDate, warning: res.warning, delivered });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo registrar la venta');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setCart({});
    setCustomer(null);
    setPaidNow(true);
    setDisplays(0);
    setDelivered(true);
    setDone(null);
  };

  if (done) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ pt: 4, textAlign: 'center' }}>
        <CheckCircle color="success" sx={{ fontSize: 80 }} />
        <Typography variant="h5" fontWeight={800}>{done.delivered ? 'Venta registrada' : 'Pedido guardado'}</Typography>
        <Typography variant="h4" fontWeight={800}>{formatMoney(done.total)}</Typography>
        <Typography color="text.secondary">
          {done.reference} · {done.paidNow ? 'Cobrada' : `A cobrar · vence ${formatDay(done.dueDate)}`}
        </Typography>
        {!done.delivered && <Alert severity="info">Cuando lo entregues, marcalo en "Entregas". Ahí se descuenta el stock.</Alert>}
        {done.warning && <Alert severity="warning">{done.warning}</Alert>}
        <Button variant="contained" size="large" sx={{ minHeight: 56, width: '100%' }} onClick={reset}>Otra venta</Button>
        <Button variant="outlined" size="large" sx={{ minHeight: 56, width: '100%' }} onClick={() => navigate('/m')}>Volver al inicio</Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <CustomerPicker value={customer} onChange={setCustomer} label="Cliente (opcional si paga ahora)" />

      <TextField
        placeholder="Buscar producto…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{ sx: { fontSize: 18, minHeight: 56 } }}
      />

      {loadingProducts ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
      ) : (
        <Stack spacing={1}>
          {products.map(p => {
            const line = cart[p.id];
            return (
              <Card key={p.id} variant="outlined" sx={{ borderColor: line ? 'primary.main' : undefined, borderWidth: line ? 2 : 1 }}>
                <Stack direction="row" alignItems="center">
                  <CardActionArea onClick={() => changeQty(p, 1)} sx={{ flex: 1 }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight={700}>{p.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatMoney(priceOf(p))}{prices[p.id] && prices[p.id].source !== 'lista' ? ` (${prices[p.id].source})` : ''} · stock {p.currentStock}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                  {line && (
                    <Stack direction="row" alignItems="center" sx={{ pr: 1 }}>
                      <IconButton onClick={() => changeQty(p, -1)} sx={{ width: 48, height: 48 }} aria-label="Quitar uno"><Remove /></IconButton>
                      <Typography variant="h6" fontWeight={800} sx={{ minWidth: 32, textAlign: 'center' }}>{line.qty}</Typography>
                      <IconButton onClick={() => changeQty(p, 1)} sx={{ width: 48, height: 48 }} aria-label="Agregar uno"><Add /></IconButton>
                    </Stack>
                  )}
                </Stack>
              </Card>
            );
          })}
          {products.length === 0 && <Typography color="text.secondary">No hay productos con ese nombre.</Typography>}
        </Stack>
      )}

      {lines.length > 0 && (
        <Card variant="outlined" sx={{ position: 'sticky', bottom: 8, boxShadow: 4 }}>
          <CardContent>
            <Stack spacing={1.5}>
              {lines.map(l => (
                <Stack key={l.product.id} direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ flex: 1 }} noWrap>{l.qty} × {l.product.name}</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={l.price}
                    onChange={e => setPrice(l.product.id, Number(e.target.value) || 0)}
                    inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                    sx={{ width: 110 }}
                    label="Precio"
                  />
                </Stack>
              ))}

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography>Exhibidores que dejás</Typography>
                <Stack direction="row" alignItems="center">
                  <IconButton onClick={() => setDisplays(d => Math.max(0, d - 1))} sx={{ width: 44, height: 44 }} aria-label="Menos exhibidores"><Remove /></IconButton>
                  <Typography variant="h6" fontWeight={800} sx={{ minWidth: 28, textAlign: 'center' }}>{displays}</Typography>
                  <IconButton onClick={() => setDisplays(d => d + 1)} sx={{ width: 44, height: 44 }} aria-label="Más exhibidores"><Add /></IconButton>
                </Stack>
              </Stack>

              <ToggleButtonGroup
                exclusive
                fullWidth
                value={delivered ? 'now' : 'later'}
                onChange={(_, v) => v && setDelivered(v === 'now')}
              >
                <ToggleButton value="now" sx={{ minHeight: 52, fontSize: 16 }}>Entrego ahora</ToggleButton>
                <ToggleButton value="later" sx={{ minHeight: 52, fontSize: 16 }}>Pedido (después)</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                exclusive
                fullWidth
                value={paidNow ? 'now' : 'account'}
                onChange={(_, v) => v && setPaidNow(v === 'now')}
              >
                <ToggleButton value="now" sx={{ minHeight: 52, fontSize: 16 }}>Paga ahora</ToggleButton>
                <ToggleButton value="account" sx={{ minHeight: 52, fontSize: 16 }}>A cuenta</ToggleButton>
              </ToggleButtonGroup>

              {paidNow ? (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {METHODS.map(m => (
                    <Chip key={m} label={m} onClick={() => setMethod(m)} color={method === m ? 'primary' : 'default'}
                      variant={method === m ? 'filled' : 'outlined'} sx={{ fontSize: 15, height: 40 }} />
                  ))}
                </Stack>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Vence en</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {DUE_OPTIONS.map(d => (
                      <Chip key={d} label={`${d} días`} onClick={() => setDueInDays(d)} color={dueInDays === d ? 'primary' : 'default'}
                        variant={dueInDays === d ? 'filled' : 'outlined'} sx={{ fontSize: 15, height: 40 }} />
                    ))}
                  </Stack>
                </Box>
              )}

              {error && <Alert severity="error">{error}</Alert>}
              <Button variant="contained" size="large" color="success" onClick={submit} disabled={saving} sx={{ minHeight: 60, fontSize: 20, fontWeight: 800 }}>
                {saving ? 'Guardando…' : `${delivered ? 'Confirmar' : 'Guardar pedido'} ${formatMoney(total)}`}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default MobileSellPage;
